import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { PublicHeader } from "@/components/PublicHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ShareDialog } from "@/components/ShareDialog";
import { InquiryDialog } from "@/components/InquiryDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { publicSiteUrl } from "@/lib/share";
import { trackEvent } from "@/lib/track";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Video, Users, Building2, ArrowLeft, Loader2, CheckCircle2, Lock, Ban, Share2, MessageSquare, Star } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { AdaptiveMediaFrame } from "@/components/AdaptiveMediaFrame";

const PH_TZ = "Asia/Manila";
const fmtRange = (startISO, endISO) => {
  if (!startISO) return "";
  try {
    const s = new Date(startISO);
    const dOpts = { timeZone: PH_TZ, year: "numeric", month: "long", day: "numeric" };
    const tOpts = { timeZone: PH_TZ, hour: "numeric", minute: "2-digit" };
    const dateStr = s.toLocaleDateString("en-PH", dOpts);
    const t = (d) => d.toLocaleTimeString("en-PH", tOpts);
    if (endISO) {
      const e = new Date(endISO);
      const endDate = e.toLocaleDateString("en-PH", dOpts);
      if (endDate === dateStr) return `${dateStr} · ${t(s)} – ${t(e)}`;
      return `${dateStr} · ${t(s)} – ${endDate} · ${t(e)}`;
    }
    return `${dateStr} · ${t(s)}`;
  } catch { return startISO; }
};

function Shell({ user, children }) {
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="event-detail-page">
      {user ? <Navbar /> : <PublicHeader />}
      <main className="flex-1 w-full mx-auto max-w-7xl px-4 sm:px-6 py-8">{children}</main>
      <SiteFooter variant={user ? "compact" : "full"} />
    </div>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const [guestStatus, setGuestStatus] = useState("GOING");
  const [justRsvped, setJustRsvped] = useState(null);
  const [g, setG] = useState({ name: "", email: "", mobile: "" });

  const load = useCallback(async () => {
    try {
      const path = user ? `/events/${id}` : `/public/events/${id}`;
      const { data } = await api.get(path);
      setEv(data);
      trackEvent("event_view", { entity_type: "event", entity_id: id });
    } catch (e) { setErr(apiError(e)); }
  }, [id, user]);

  useEffect(() => { if (user !== undefined) load(); }, [load, user]);

  // Public-safe OG/social metadata (mirrors backend /api/og/event/{id}); reverted on unmount.
  useEffect(() => {
    if (!ev) return;
    const prevTitle = document.title;
    const t = `${ev.event_type_label} — ${ev.title}`;
    const desc = [ev.event_type_label, fmtRange(ev.start_at, ev.end_at), ev.venue || ev.city || (ev.is_online ? "Online" : "")].filter(Boolean).join(" • ");
    const url = `${publicSiteUrl()}/event/${id}`;
    document.title = t;
    const set = (k, v, prop = true) => { if (!v) return; const a = prop ? "property" : "name"; let el = document.head.querySelector(`meta[${a}="${k}"]`); if (!el) { el = document.createElement("meta"); el.setAttribute(a, k); document.head.appendChild(el); } el.setAttribute("content", v); };
    set("og:title", t); set("og:description", desc); set("og:type", "website"); set("og:url", url);
    if (ev.cover_image) set("og:image", ev.cover_image);
    return () => { document.title = prevTitle; };
  }, [ev, id]);

  const doRsvp = async (status, guest) => {
    setBusy(true);
    try {
      const body = guest ? { status, ...guest } : { status };
      const { data } = await api.post(`/events/${id}/rsvp`, body);
      setEv(data);
      setGuestOpen(false);
      if (!user) setJustRsvped(status);
      toast.success(status === "GOING" ? "You're going! 🎉" : "Marked as interested");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const onRsvpClick = (status) => {
    if (user) return doRsvp(status);
    setGuestStatus(status); setGuestOpen(true);   // guests: minimal info, no forced registration
  };
  const submitGuest = () => {
    if (!g.name.trim() || !g.email.trim()) { toast.error("Please enter your name and email"); return; }
    doRsvp(guestStatus, g);
  };
  const withdraw = async () => {
    if (!user) { setEv({ ...ev, my_rsvp_status: null, my_rsvp: false }); return; }
    setBusy(true);
    try { const { data } = await api.delete(`/events/${id}/rsvp`); setEv(data); toast.success("RSVP withdrawn"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const openShare = () => { trackEvent("event_share", { entity_type: "event", entity_id: id }); setShareOpen(true); };

  if (err) return <Shell user={user}><div className="text-center py-20"><p className="text-slate-500">{err}</p><Link to="/marketplace?mode=events" className="text-[#0F2C59] font-semibold mt-3 inline-block">Back to Events</Link></div></Shell>;
  if (!ev) return <Shell user={user}><p className="text-sm text-slate-400">Loading…</p></Shell>;

  const cancelled = ev.status === "CANCELLED";
  const isPublicEvent = ev.visibility === "PUBLIC" && ev.status === "PUBLISHED";
  const eventJsonLd = isPublicEvent ? {
    "@context": "https://schema.org",
    "@type": "Event",
    name: ev.title,
    startDate: ev.start_at || undefined,
    endDate: ev.end_at || undefined,
    eventAttendanceMode: ev.is_online
      ? "https://schema.org/OnlineEventAttendanceMode"
      : "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: cancelled ? "https://schema.org/EventCancelled" : "https://schema.org/EventScheduled",
    ...(ev.description ? { description: ev.description } : {}),
    ...(ev.cover_image ? { image: [ev.cover_image] } : {}),
    location: ev.is_online
      ? { "@type": "VirtualLocation", url: `${publicSiteUrl()}/event/${id}` }
      : { "@type": "Place", name: ev.venue || ev.city || "Philippines",
          address: [ev.venue, ev.city, ev.province].filter(Boolean).join(", ") || "Philippines" },
    url: `${publicSiteUrl()}/event/${id}`,
    organizer: { "@type": "Organization", name: "PropertyConnect", url: publicSiteUrl() },
  } : null;

  return (
    <Shell user={user}>
      <Seo
        robots={isPublicEvent ? "index,follow" : "noindex,follow"}
        canonical={`/event/${id}`}
        description={[ev.event_type_label, fmtRange(ev.start_at, ev.end_at), ev.venue || ev.city || (ev.is_online ? "Online" : "")].filter(Boolean).join(" • ")}
        jsonLd={eventJsonLd}
      />
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white">
            {ev.cover_image && !imgError ? (
              <AdaptiveMediaFrame
                src={ev.cover_image}
                alt={ev.title}
                policy="preservation"
                tone="light"
                context="detail"
                rounded=""
                loading="eager"
                onError={() => setImgError(true)}
                data-testid="event-cover"
              />
            ) : (
              <div className="w-full h-64 bg-gradient-to-br from-[#0F2C59] to-[#1a3d6e] flex flex-col items-center justify-center gap-2" data-testid="event-cover-placeholder">
                <Calendar className="h-14 w-14 text-[#C79A3B]/70" />
                <span className="text-white/70 text-sm font-semibold">PropertyConnect Event</span>
              </div>
            )}
            <div className="p-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#0F2C59] text-white" data-testid="event-type-badge">{ev.event_type_label}</span>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ev.event_format_label}</span>
                {ev.visibility === "PUBLIC" ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Public</span> : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Network Only</span>}
                {cancelled && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 flex items-center gap-1"><Ban className="h-3 w-3" /> Cancelled</span>}
                {ev.is_past && !cancelled && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Ended</span>}
              </div>
              <h1 className="font-display text-3xl font-bold text-slate-900 mt-3" data-testid="event-title">{ev.title}</h1>
              {ev.description && <p className="text-slate-600 mt-3 whitespace-pre-line">{ev.description}</p>}

              {(ev.developer_name || ev.project_name) && (
                <div className="mt-4 flex items-center gap-2 text-sm text-slate-600"><Building2 className="h-4 w-4 text-primary" /> {[ev.project_name, ev.developer_name].filter(Boolean).join(" · ")}</div>
              )}
              {ev.property && (
                <Link to={`/listing/${ev.property.id}`} className="mt-3 flex items-center gap-3 rounded-xl border border-slate-200 p-3 hover:border-primary" data-testid="event-linked-property">
                  <img src={ev.property.image} alt="" className="h-14 w-14 rounded-lg object-cover" />
                  <span><span className="block text-sm font-semibold text-slate-800">{ev.property.title}</span><span className="block text-xs text-slate-500">{ev.property.city}</span></span>
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
            <div className="flex items-start gap-2.5"><Calendar className="h-5 w-5 text-[#C79A3B] mt-0.5" /><div><div className="text-sm font-semibold text-slate-800" data-testid="event-when">{fmtRange(ev.start_at, ev.end_at)}</div><div className="text-xs text-slate-500">Philippine Time</div></div></div>
            {ev.venue || ev.city ? <div className="flex items-start gap-2.5"><MapPin className="h-5 w-5 text-[#C79A3B] mt-0.5" /><div className="text-sm text-slate-700">{[ev.venue, ev.city, ev.province].filter(Boolean).join(", ")}</div></div> : null}
            {ev.is_online && (
              <div className="flex items-start gap-2.5"><Video className="h-5 w-5 text-[#C79A3B] mt-0.5" />
                {ev.meeting_link ? <a href={ev.meeting_link} target="_blank" rel="noreferrer" data-testid="event-meeting-link" className="text-sm text-[#0F2C59] font-semibold hover:underline break-all">{ev.meeting_link}</a>
                  : <div className="text-sm text-slate-500 flex items-center gap-1" data-testid="event-meeting-locked"><Lock className="h-3.5 w-3.5" /> Online Event · Meeting details provided after RSVP</div>}
              </div>
            )}
            <div className="flex items-center gap-4 text-sm text-slate-600 pt-1">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#C79A3B]" /> <b data-testid="event-going-count">{ev.going_count}</b> going</span>
              <span className="flex items-center gap-1.5"><Star className="h-4 w-4 text-[#C79A3B]" /> <b data-testid="event-interested-count">{ev.interested_count}</b> interested</span>
            </div>
          </div>

          {ev.organizer && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Organizer</p>
              <div className="flex items-center gap-3 mt-2">
                <img src={ev.organizer.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
                <div className="min-w-0">
                  {ev.organizer.public_profile_path
                    ? <Link to={ev.organizer.public_profile_path} data-testid="event-organizer-link" className="text-sm font-semibold text-[#0F2C59] hover:underline truncate block">{ev.organizer.name}</Link>
                    : <span className="text-sm font-semibold text-slate-800 truncate block">{ev.organizer.name}</span>}
                  <span className="text-xs text-slate-500">{ev.organizer.role_label}</span>
                </div>
              </div>
            </div>
          )}

          {ev.is_organizer ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-5 text-center space-y-3">
              <p className="text-sm text-slate-500">You're the organizer.</p>
              <Link to="/my-events" data-testid="event-manage-link" className="block text-sm text-[#0F2C59] font-semibold hover:underline">Manage in My Events →</Link>
              <Button data-testid="event-organizer-share-btn" onClick={openShare} variant="outline" className="w-full rounded-xl border-[#0F2C59] text-[#0F2C59]"><Share2 className="h-4 w-4 mr-1.5" /> Share Event</Button>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
              {cancelled ? (
                <div className="rounded-xl bg-red-50 text-red-600 p-3 text-sm text-center">This event has been cancelled.</div>
              ) : ev.is_past ? (
                <div className="rounded-xl bg-slate-100 text-slate-500 p-3 text-sm text-center">This event has ended.</div>
              ) : ev.my_rsvp_status === "GOING" ? (
                <>
                  <div className="rounded-xl bg-emerald-50 text-emerald-700 p-3 text-sm text-center flex items-center justify-center gap-2" data-testid="event-rsvp-confirmed"><CheckCircle2 className="h-4 w-4" /> You're attending</div>
                  <div className="text-xs text-slate-500 text-center">{fmtRange(ev.start_at, ev.end_at)} · Philippine Time</div>
                  <Button data-testid="event-withdraw-btn" onClick={withdraw} disabled={busy} variant="outline" className="w-full rounded-xl">Change / Cancel RSVP</Button>
                </>
              ) : (!user && justRsvped) ? (
                <div className="rounded-xl bg-emerald-50 text-emerald-700 p-3 text-sm text-center flex items-center justify-center gap-2" data-testid="event-guest-rsvp-confirmed"><CheckCircle2 className="h-4 w-4" /> RSVP recorded{justRsvped === "GOING" ? " — you're going" : " as interested"}. Details will be sent to your email.</div>
              ) : (
                <>
                  <p className="text-sm font-semibold text-slate-800">Interested in attending?</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button data-testid="event-going-btn" onClick={() => onRsvpClick("GOING")} disabled={busy} className="h-11 bg-[#C79A3B] text-[#0F2C59] hover:bg-[#C79A3B]/90 font-semibold rounded-xl">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Going"}</Button>
                    <Button data-testid="event-interested-btn" onClick={() => onRsvpClick("INTERESTED")} disabled={busy} variant="outline" className="h-11 border-[#0F2C59] text-[#0F2C59] font-semibold rounded-xl">{ev.my_rsvp_status === "INTERESTED" ? "✓ Interested" : "Interested"}</Button>
                  </div>
                </>
              )}
              <Button data-testid="event-inquiry-btn" onClick={() => setInquiryOpen(true)} variant="outline" className="w-full rounded-xl border-slate-300"><MessageSquare className="h-4 w-4 mr-1.5" /> Ask About This Event</Button>
              <Button data-testid="event-share-btn" onClick={openShare} variant="ghost" className="w-full rounded-xl text-slate-600"><Share2 className="h-4 w-4 mr-1.5" /> Share</Button>
            </div>
          )}
        </div>
      </div>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} url={`${publicSiteUrl()}/event/${id}`}
        title={ev.title} text={`${ev.event_type_label} • ${fmtRange(ev.start_at, ev.end_at)}`}
        heading="Share this event" subtitle="Invite others to this event" entityId={id} />

      <InquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} listingId={id}
        endpoint={`/public/events/${id}/inquiries`} mode="inquiry"
        heading="Ask About This Event" subtitle={ev.title} />

      <Dialog open={guestOpen} onOpenChange={setGuestOpen}>
        <DialogContent className="max-w-md" data-testid="event-guest-rsvp-dialog">
          <DialogHeader><DialogTitle>RSVP to {ev.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Just a few details to confirm your spot — no account needed.</p>
            <div><Label>Name</Label><Input data-testid="guest-rsvp-name" value={g.name} onChange={(e) => setG({ ...g, name: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input data-testid="guest-rsvp-email" type="email" value={g.email} onChange={(e) => setG({ ...g, email: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Mobile <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="guest-rsvp-mobile" value={g.mobile} onChange={(e) => setG({ ...g, mobile: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="guest-rsvp-submit" onClick={submitGuest} disabled={busy} className="w-full bg-[#C79A3B] text-[#0F2C59] hover:bg-[#C79A3B]/90 font-semibold rounded-xl">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm ${guestStatus === "GOING" ? "Going" : "Interested"}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
